// store.js
import { reactive } from 'vue'
import { onPageHost } from './utils/local-host.js'

export const store = reactive({
  querent_carat_balance: 0,
  // Has a server ever told us the balance this session? querent_id is
  // rehydrated SYNCHRONOUSLY from localStorage, so the balance chip mounted
  // against the literal 0 above and then counted up to the truth once the async
  // fetch landed — a wrong number, shown first. Set true by the three fetches
  // that carry a balance (login, getQuerent, start_session); every other write
  // is a post-action sync that necessarily follows one of them.
  querent_carat_balance_loaded: false,
  current_reader_subdomain:'',
  current_reader:0,
  current_reader_name:'',
  querent_id: '',
  querent_name: '',
  // The /seeker/<slug> handle they hold. Lets the name picker price a card
  // they already own at zero instead of offering to charge again.
  querent_name_slug: null,
  querent_birthdate: '',
  // Care Package alias (a real name), signed on packages that leave the platform; kept on the Mirror.
  querent_first_name: '',
  querent_last_name: '',
  querent_birthtime: '',
  querent_birth_location_lat: '',
  querent_birth_location_long: '',
  querent_email: '',
  // The seeker's Mirror avatar URL — surfaced in the top-right Account chip.
  querent_avatar_url: '',
  // Toggles the global Account modal (top-right avatar/name → settings + security).
  account_modal_open: false,
  querent_subscribed: false,
  querent_subscription_expiration: '',
  session_id: '',
  reading_session_id: '',
  reading_sessions: [],
  login_current_tab: 'login',
  // An email the app already knows when it opens the login modal (the care
  // package door recognises the recipient's address). Read once by
  // AuthenticateUser when the modal opens, then cleared.
  login_prefill_email: '',
  // "Email me a code": the emailed link's token, handed to the sign-in form.
  login_signin_token: '',
  cf:'',
  is_loading: false,
  is_streaming_chat: false,
  // Global messaging — the icon + unread badge ride on every page (App.vue);
  // message_count drives the badge, messages_open toggles the global overlay.
  message_count: 0,
  messages_open: false,
  // The card shown in the global Card Detail modal (meaning + reader symbolism).
  card_detail: null,
  conversation_state: '',
  // Doorman v1 — soft nudge from the BE Interview state's
  // refine_instructions step. When true, the FE visually emphasizes
  // the Begin Reading button (the button is always rendered and
  // clickable regardless; this is purely a visual signal).
  ready_for_reading: false,
  latest_reading_session:  '',
  latest_reading_session_type:  '',
  // Which game component to mount for the current reading session —
  // 'one' (OneCardReading) or 'four' (FourCardReading). Derived from
  // the active session's spread.card_count when the BE serves it on
  // session start/resume; defaults to 'four' so legacy paths that
  // don't carry spread info still get the historical layout.
  reading_type: 'four',
  latest_reading_session_is_paid: false,
  // Holds the reading id (matches ?reading=) when a reading is opened FROM the
  // Journal, so finishing/closing it returns to the Journal instead of the reader
  // page. Keyed to the id so a stale flag can't bounce an unrelated reading.
  reading_return_to_journal: '',
  latest_reading_likes:  '',
  latest_reading_views:  '',
  is_latest_reading_session_shared: false,
  latest_reading_date: '',
  // Talk it through: where the conversation after the reading stands, the
  // server's word (utils/talk-it-through.js): {reading_id, started, used,
  // allowed, remaining, next_round, more_price, card_offer, closed, ...}.
  talk: null,
  // The cards pulled while talking it through: [{ id, kind, card, image, reversed, question, line, reading }].
  talk_cards: [],
  // The seeker's own rating of a reading, by the id the crystals rate it under
  // (its uuid, or a shared short id): the crystals show it on every mount,
  // reopened (load-reading-session's my_rating) or after talking it through.
  reading_ratings: {},
  // Care Package: who the live reading is for ({is_gift, for_name, for_querent, signed_as, from_name, gift_note, reading_session_id}).
  reading_gift: null,
  // A care package open in its recipient's hands (the room): the panel shows
  // all of it, signed in or not (utils/reading-lock).
  gift_room_open: false,
  // The server confirmed the viewer holds a live "Send a link" token for the
  // reading on screen (load-reading-session answered access: 'invite').
  link_holder: false,
  // Her own reading started from the gift room (recipient journey R5.2, R5.8).
  intro_own_reading: false,
  // Stage 4 (recipient journey Part 7): her reading for one of the people she
  // named, started from the room. It keeps the introduction; it ends on one
  // chip, "Send it to Mom", and its rating sits on the send screen (R7.5, R7.9).
  intro_people_reading: false,
  // The gift room's lead and rating ({ short, note, from_name, portrait }), set
  // while a package is open in its room (recipient journey R4.13, R4.15).
  gift_lead: null,
  // A gift's own version: her card's short phrase by position reference
  // (positions[].line, 2026-10-19). Data only; the room does not show it yet.
  reading_position_lines: {},
  // A recipient's introduction (server-computed, R4.21): while true in a gift
  // room, the rail, tabs, bottom bar, Reveal Myself and the balance are not
  // rendered (R4.22). Part 8 decides when it ends.
  introduction: false,
  just_registered: false,
  pendingCommunityIntent: null,
  // What the seeker was reaching for when they hit the login gate:
  // { theme, resume }. The theme picks the hero copy; the resume is replayed
  // after login. Set via utils/request-auth, consumed by takeAuthIntent().
  authIntent: null,
  astrology_ready: false,
  global_actions: ['login', 'logout', 'reading_history', 'account', 'help'],

  // ─── Reader State ────────────────────────────────────────────────── //
  isReader: null,
  reader: null,
  // Superuser "edit another reader" (Studio ONLY). In-memory + ephemeral on
  // purpose: it resets to '' on every refresh and is cleared the moment you
  // leave the Studio, so impersonation can NEVER bleed into Altar/Quest or
  // survive a reload. The axios interceptor only sends the act-as header while
  // this is set; nothing else reads it.
  studioActingAsReader: '',
  readerSkills: [],
  readerSkillVariants: [],   // Phase 13 — list of {id, skill_key, variant_key, is_active, ...}
  // What the CURRENT reader charges: { carats, pay_at_reception } straight off
  // reader_detail. null until a reader has loaded, so a price surface can tell
  // "not known yet" from "free". readerSkillVariants above is NOT a substitute:
  // Studio code is the only thing that ever fills it, so on the seeker's own
  // reader page it is empty and every price read from it was the floor.
  reading_price: null,

  // ─── Reader Onboarding ──────────────────────────────────────────── //
  onboarding: {
    sessionKey: null,
    status: null,
    isQuerentUpgrade: false,
    querentName: null,
    conversationMessages: [],
    extractionComplete: false,
    extractedData: {},
    nameSuggestions: [],
    previousNames: [],
    chosenName: null,
    nameSlug: null,
    nameRefreshCount: 0,
    nameRefreshRemaining: 5,
    activeWidget: null,
    nameRefinementCount: 0,
    imageUrl: null,
    imageUrlFeminine: null,
    imageUrlMasculine: null,
    confirmedImageUrl: null,
    imageRefreshCount: 0,
    imageRefreshRemaining: 5,
    personaSummary: null,
    samplePhrases: [],
    publicUrl: null,
    conversationProgress: 0,
    speakingStyleDescription: '',
    voiceId: '',
    avatarTemplates: { image: [], style: [] },
    avatarTemplatesLoaded: false,
    selectedImageTemplate: null,
    selectedStyleTemplate: null,
    selectedChakraColor: null,
    customImages: [],
    imageGenerating: false,
    claimedEmail: '',
    accountAlreadyExisted: false,
  }
})

export const static_store = {
  // onPageHost: a localhost URL follows the page's host, so a phone on the home
  // network reaches the dev server (utils/local-host.js). Production is untouched.
  shareApiUrl: onPageHost(window.env?.VUE_APP_DJANGO_SHARE_URL || process.env.VUE_APP_DJANGO_SHARE_URL),
  apiUrl: onPageHost(window.env?.VUE_APP_DJANGO_API_URL || process.env.VUE_APP_DJANGO_API_URL),
  webSocketUrl: onPageHost(window.env?.VUE_APP_WEB_SOCKET_URL || process.env.VUE_APP_WEB_SOCKET_URL),
  stripe_public_key: window.env?.VUE_APP_STRIPE_PUBLIC_KEY || process.env.VUE_APP_STRIPE_PUBLIC_KEY,
  azure_public_bucket: window.env?.VUE_APP_AZURE_PUBLIC_BUCKET || process.env.VUE_APP_AZURE_PUBLIC_BUCKET,
  front_end_url: window.env?.VUE_APP_FRONTEND_URL || process.env.VUE_APP_FRONTEND_URL,
  // Birthplace map tiles (BirthplacePicker). Optional: with no key the map
  // falls back to OpenStreetMap, which is fine for a dev box and blocked for
  // the shipped app. Same ``process``-less read as the custom-deck override.
  maptiler_key: window.env?.VUE_APP_MAPTILER_KEY || '',
  // Continue with Google (2026-10-31): the Web client ID from Google Cloud
  // Console (public). Empty = no Google button on the sign-in form. The
  // backend needs the same value as GOOGLE_OAUTH_CLIENT_ID.
  google_oauth_client_id: window.env?.VUE_APP_GOOGLE_OAUTH_CLIENT_ID || '',
  // Optional override for the Custom Deck builder endpoints. When set,
  // FE points the 6 custom-deck calls at this URL instead of ``apiUrl``.
  // Lets local dev exercise the Custom Deck flow against prod data
  // without changing the shared ``apiUrl``. The api-service also injects
  // ``X-Mystic-Source: localhost`` so MCP can filter dev rows for
  // cleanup. Phase 5 of CUSTOM_DECK_BUILDER plan.
  // Skip the ``process.env`` fallback the other entries use — this
  // var isn't always defined in .env, and ``process`` is undefined
  // in the Vite browser runtime, which would ReferenceError and
  // break the whole app. ``window.env`` is enough; empty = use apiUrl.
  customDeckApiUrl: (typeof window !== 'undefined' && window.env?.VUE_APP_DJANGO_API_URL_CUSTOM_DECK) || ''
};
